import { CurrencyInput, DataGrid, Input, InputV2, TooltipV2 } from '@cimplico/components';
import { NatureEnum } from '../../../types/nature';
import {
    GenericWorksheet,
    ReconciliationWorksheet,
    WorksheetEntryTypeEnum,
    WorksheetEntryV2,
    WorksheetTypeEnum,
} from '../../../types/worksheet';
import { Controller, useForm } from 'react-hook-form';
import AttachmentMenu from './attachments/AttachmentMenu';
import { CreateWorksheetEntryRequest } from '../../../queries/use-create-worksheet-entry';
import EntryDescriptionSelect from './EntryDescriptionSelect';
import { isNullOrUndefined } from '../../new-workpaper/utils';
import { LockClosedIcon } from '@heroicons/react/20/solid';

type NewOrEditEntryRowProps = {
    id: string;
    mode: 'EDIT' | 'CREATE';
    worksheet: GenericWorksheet | ReconciliationWorksheet;
    entry?: Partial<WorksheetEntryV2>;
    dragging?: boolean;
    onSave: (data: CreateWorksheetEntryRequest) => void;
    disableAttachments: boolean;
    showAttachmentWindow: boolean;
    onAttachmentWindowToggle: (open: boolean) => void;
};

type FormValues = {
    date: string;
    amount?: number;
    nature?: NatureEnum;
    description?: string;
    attachments?: string[];
};

function NewOrEditEntryRow({
    id,
    mode,
    worksheet,
    entry = {},
    dragging,
    onSave,
    disableAttachments,
    onAttachmentWindowToggle,
    showAttachmentWindow,
}: NewOrEditEntryRowProps): JSX.Element {
    const isRec = worksheet.type === WorksheetTypeEnum.RECONCILIATION;

    const form = useForm<FormValues>({
        defaultValues: {
            date: entry.date?.slice(0, 10),
            amount: isNullOrUndefined(entry.amount) ? undefined : (entry.amount || 0) / 100,
            nature: entry.nature || undefined,
            description: entry.description || undefined,
            attachments: entry.attachments,
        },
    });

    function handleSubmit(data: FormValues): void {
        const { date, amount, description, nature, attachments } = data;

        onSave({
            entry: {
                date,
                amount: isRec ? +(amount || 0) : undefined,
                description,
                nature,
                attachments,
                type: isRec ? WorksheetEntryTypeEnum.RECONCILIATION_ENTRY : WorksheetEntryTypeEnum.GENERIC_ENTRY,
            },
        });
    }

    const Row = (
        <div>
            {entry.source && (
                <div className="absolute left-8 z-50">
                    <TooltipV2 message="This entry has been created by the system.">
                        <LockClosedIcon className="text-gray-800 h-4 w-4 mt-3" />
                    </TooltipV2>
                </div>
            )}

            <DataGrid.Row id={id} disabled={mode === 'CREATE'} preventDrag={mode === 'CREATE'}>
                <div>
                    <Controller
                        control={form.control}
                        name="date"
                        rules={{ required: true }}
                        render={({ fieldState, field: { value, onChange } }): JSX.Element => {
                            return (
                                <Input
                                    type="date"
                                    size="small"
                                    onChange={onChange}
                                    isError={Boolean(fieldState.error)}
                                    defaultValue={value}
                                />
                            );
                        }}
                    />
                </div>
                <p className="text-gray-700 text-xs">{entry.reference ? `ENT#${entry.reference.toString().padStart(3, '0')}` : '-'}</p>
                <div className="w-full">
                    <Controller
                        name="description"
                        control={form.control}
                        render={({ field: { onChange, value } }): JSX.Element => {
                            return <EntryDescriptionSelect value={value} onChange={onChange} />;
                        }}
                    />
                </div>
                <div className={!isRec ? 'mr-6' : ''}>
                    <AttachmentMenu
                        attachments={form.getValues()?.attachments ?? []}
                        worksheetFiles={worksheet.files}
                        defaultFolderId={worksheet.id}
                        onAttach={(val): void => {
                            form.setValue('attachments', val);
                        }}
                        disabled={disableAttachments}
                        isOpen={showAttachmentWindow}
                        onOpenToggle={onAttachmentWindowToggle}
                    />
                </div>
                {worksheet.type === WorksheetTypeEnum.RECONCILIATION && (
                    <Controller
                        name="amount"
                        control={form.control}
                        render={({ field, fieldState }): JSX.Element => {
                            return (
                                <TooltipV2 message="This is a system entry, the amount cannot be edited." disabled={!entry.source}>
                                    <CurrencyInput
                                        isError={Boolean(fieldState.error)}
                                        placeholder="0.00"
                                        textAlign="right"
                                        onChange={field.onChange}
                                        value={field.value?.toString()}
                                        size="small"
                                        disabled={Boolean(entry.source)}
                                    >
                                        <Controller
                                            name="nature"
                                            control={form.control}
                                            rules={{ required: true }}
                                            render={({ field }): JSX.Element => {
                                                return (
                                                    <Input.Dropdown
                                                        options={[
                                                            { label: 'Dr', value: NatureEnum.DEBIT },
                                                            { label: 'Cr', value: NatureEnum.CREDIT },
                                                        ]}
                                                        value={field.value}
                                                        name="nature"
                                                        onChange={field.onChange}
                                                    ></Input.Dropdown>
                                                );
                                            }}
                                        />
                                    </CurrencyInput>
                                </TooltipV2>
                            );
                        }}
                    />
                )}
            </DataGrid.Row>
        </div>
    );

    return dragging ? (
        Row
    ) : (
        <form id="new-worksheet-entry" onSubmit={form.handleSubmit(handleSubmit)}>
            {Row}
        </form>
    );
}

export default NewOrEditEntryRow;
